import { Mail, Phone, Clock, Globe2 } from "lucide-react";
import { CONTACT_INFO, telHref } from "@/data/content";

/**
 * Contact Information Card — reusable across Contact page, CTAs and mobile section.
 * Variant: 'light' (default, on white) or 'dark' (on navy).
 */
export default function ContactInfoCard({ variant = "light", className = "", testId = "contact-info-card" }) {
  const isDark = variant === "dark";
  const border  = isDark ? "border-white/15" : "border-[var(--dm-border)]";
  const bg      = isDark ? "bg-white/[0.04]" : "bg-white";
  const headTxt = isDark ? "text-white/60"   : "text-[var(--dm-muted)]";
  const bodyTxt = isDark ? "text-white"      : "text-[var(--dm-navy)]";
  const muted   = isDark ? "text-white/70"   : "text-[var(--dm-muted)]";
  const accent  = "text-[var(--dm-emerald)]";
  const iconBg  = isDark ? "bg-white/10 text-white" : "bg-[var(--dm-navy)] text-white";

  const Row = ({ icon: Icon, label, children }) => (
    <div className="flex gap-4">
      <div className={`w-10 h-10 grid place-items-center shrink-0 ${iconBg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${headTxt}`}>{label}</div>
        <div className={`mt-1 ${bodyTxt}`}>{children}</div>
      </div>
    </div>
  );

  return (
    <div data-testid={testId} className={`border ${border} ${bg} p-8 ${className}`}>
      <div className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${accent}`}>Contact Information</div>
      <h3 className={`font-serif text-2xl mt-3 ${bodyTxt}`}>Talk to a Database Specialist</h3>

      <div className="mt-8 space-y-6">
        <Row icon={Mail} label="Email">
          <a
            data-testid={`${testId}-email`}
            href={`mailto:${CONTACT_INFO.email}?subject=New%20Website%20Enquiry%20-%20DataMeridian%20Consulting`}
            className={`font-serif text-lg hover:${accent} break-all`}
          >
            {CONTACT_INFO.email}
          </a>
        </Row>

        <Row icon={Phone} label="Phone">
          <div className="flex flex-col gap-1">
            <a
              data-testid={`${testId}-phone-primary`}
              href={telHref(CONTACT_INFO.phonePrimary)}
              className={`font-serif text-lg hover:${accent}`}
            >
              {CONTACT_INFO.phonePrimary}
            </a>
            <a
              data-testid={`${testId}-phone-alt`}
              href={telHref(CONTACT_INFO.phoneAlternate)}
              className={`font-serif text-lg hover:${accent}`}
            >
              {CONTACT_INFO.phoneAlternate}
            </a>
          </div>
        </Row>

        <Row icon={Clock} label="Availability">
          <div className={`text-sm ${muted} leading-relaxed`}>
            {CONTACT_INFO.availabilityDays}<br />
            {CONTACT_INFO.availabilityHours}
          </div>
        </Row>

        <Row icon={Globe2} label="Delivery">
          <div className={`text-sm ${muted}`}>{CONTACT_INFO.availabilityNote}</div>
        </Row>
      </div>
    </div>
  );
}
